'use client';

import { useLogic } from './logic';
import { Avatar, Box, Typography, Link, Paper } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  AdvancedMarker,
  APIProvider,
  ControlPosition,
  Map,
  MapControl,
} from '@vis.gl/react-google-maps';

import { DirectionsFork, HospitalBuilding } from 'mdi-material-ui';

type Props = {
  address: string;
  postCode: string;
  stationName: string;
  mapCenter: { lat?: number; lng?: number };
};

export const ListingMapDetail = ({
  address,
  postCode,
  stationName,
  mapCenter,
}: Props) => {
  return (
    <APIProvider apiKey={process.env.NEXT_PUBLIC_MAPS_API_KEY as string}>
      <MapWrapper
        address={address}
        postCode={postCode}
        stationName={stationName}
        mapCenter={mapCenter}
      />
    </APIProvider>
  );
};

const MapContent = ({
  address,
  postCode,
  stationName,
  isMobile,
  geocodedCenter,
}: Props & {
  isMobile: boolean;
  geocodedCenter: { lat: number; lng: number } | undefined;
}) => {
  return (
    <Map
      mapId={'station-map'}
      defaultCenter={geocodedCenter}
      defaultZoom={16}
      style={{ width: '100%', aspectRatio: '16 / 9' }}
      mapTypeControl={false}
      streetViewControl={false}
      fullscreenControl={false}
      zoomControl={false}
    >
      <MapControl position={ControlPosition.TOP_LEFT}>
        <Paper
          elevation={2}
          sx={{
            padding: isMobile ? 1 : 2,
            margin: 1,
            backgroundColor: 'white',
            borderRadius: 1,
          }}
        >
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                  }}
                >
                  <Typography
                    variant="JP/Body/s/Regular/Normal"
                    sx={{ fontWeight: 'bold' }}
                  >
                    {stationName}
                  </Typography>
                  <Typography variant="JP/Body/s/Regular/Normal">
                    {postCode ? `${postCode} ${address}` : address}
                  </Typography>
                </Box>
              }

              <Link
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                  textDecoration: 'none',
                }}
              >
                <DirectionsFork
                  sx={{ fontSize: 28, transform: 'scale(-1, 1)' }}
                />
                <Typography variant="JP/Body/s/Regular/Normal">
                  ルート
                </Typography>
              </Link>
            </Box>
          )}

          <Link
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: '0.75rem',
              textDecoration: 'none',
            }}
          >
            <OpenInNewIcon sx={{ fontSize: 16 }} />
            <Typography variant="JP/Body/s/Regular/Normal">
              拡大地図を表示
            </Typography>
          </Link>
        </Paper>
      </MapControl>
      <AdvancedMarker position={geocodedCenter}>
        <Avatar
          sx={{
            background: '#33BEB9',
            border: 'solid 2px #FFFFFF',
            boxShadow: '6px 5px 5px -1px rgba(0, 0, 0, 0.4)',
            transition: 'transform 0.2s ease-in-out',
            cursor: 'pointer',
          }}
        >
          <HospitalBuilding sx={{ color: '#FFFFFF' }} />
        </Avatar>
      </AdvancedMarker>
    </Map>
  );
};

function MapWrapper({ address, postCode, stationName, mapCenter }: Props) {
  const { isMobile, geocodedCenter } = useLogic({
    address,
    mapCenter,
  });

  if (!address) {
    return <></>;
  }

  if (!geocodedCenter) {
    return <></>;
  }

  return (
    <MapContent
      address={address}
      postCode={postCode}
      stationName={stationName}
      isMobile={isMobile}
      geocodedCenter={geocodedCenter}
      mapCenter={mapCenter}
    />
  );
}
